import React, { useState, useEffect } from 'react';
import { Calendar, Download, Filter, Eye, Target, AlertTriangle } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { useSupabaseAuth, useAuditSessions } from '../../hooks/useSupabase';
import { formatTimestamp } from '../../utils/formatters';
import { getRiskLevel, getRiskColor } from '../../utils/riskCalculations';
import type { AuditSession } from '../../types/database';

export const AssessmentHistory: React.FC = () => {
  const { user } = useSupabaseAuth();
  const { sessions, loading, getSessions } = useAuditSessions(user?.id);
  const [filteredSessions, setFilteredSessions] = useState<AuditSession[]>([]);
  const [selectedPersona, setSelectedPersona] = useState<string>('all');
  const [selectedSession, setSelectedSession] = useState<AuditSession | null>(null);

  useEffect(() => {
    if (user) {
      getSessions();
    }
  }, [user, getSessions]);

  useEffect(() => {
    if (selectedPersona === 'all') {
      setFilteredSessions(sessions);
    } else {
      setFilteredSessions(sessions.filter(session => session.persona === selectedPersona));
    }
  }, [sessions, selectedPersona]);

  const exportSession = (session: AuditSession) => {
    const exportData = {
      sessionId: session.id,
      timestamp: session.created_at,
      targetUrl: session.target_url,
      targetModel: session.target_model,
      persona: session.persona,
      basePrompt: session.base_prompt,
      results: session.results
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `red-set-assessment-${session.id.slice(0, 8)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getOverallRisk = (session: AuditSession): number => {
    if (session.results && typeof session.results === 'object' && 'overallRisk' in session.results) {
      return session.results.overallRisk as number;
    }
    return 0;
  };

  if (loading) {
    return (
      <Card variant="red">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-red-100 rounded w-1/3"></div>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-16 bg-red-50 rounded"></div>
            ))}
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card variant="red">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900 flex items-center">
            <Calendar className="h-5 w-5 mr-2" />
            Assessment History
          </h2>
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <Filter className="h-4 w-4 text-gray-500" />
              <select
                value={selectedPersona}
                onChange={(e) => setSelectedPersona(e.target.value)}
                className="px-3 py-1 border border-red-500/30 rounded-md text-sm focus:ring-2 focus:ring-red-500"
              >
                <option value="all">All Personas</option>
                <option value="jailbreaker">Jailbreaker</option>
                <option value="bias-probe">Bias Probe</option>
                <option value="misinformation-tester">Misinformation</option>
                <option value="toxicity-inducer">Toxicity</option>
                <option value="privacy-extractor">Privacy</option>
              </select>
            </div>
          </div>
        </div>

        {filteredSessions.length === 0 ? (
          <div className="text-center py-8">
            <Target className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Assessments Found</h3>
            <p className="text-gray-600">
              {selectedPersona === 'all' 
                ? 'Start by running your first red team assessment.'
                : `No assessments found for ${selectedPersona} persona.`
              }
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredSessions.map((session) => {
              const overallRisk = getOverallRisk(session);
              const riskLevel = getRiskLevel(overallRisk);
              const riskColor = getRiskColor(overallRisk);
              
              return (
                <div
                  key={session.id}
                  className="border border-red-500/30 rounded-lg p-4 hover:bg-red-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className="font-medium text-gray-900">
                          {session.persona.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          riskColor === 'red' ? 'bg-red-100 text-red-800' :
                          riskColor === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {riskLevel} ({overallRisk}/100)
                        </span>
                        <span className="text-sm text-gray-500">
                          {formatTimestamp(session.created_at)}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600">
                        <p><strong>Model:</strong> {session.target_model}</p>
                        <p><strong>Prompt:</strong> {session.base_prompt.slice(0, 100)}...</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => setSelectedSession(session)}
                      >
                        <Eye className="h-4 w-4" />
                        View
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => exportSession(session)}
                      >
                        <Download className="h-4 w-4" />
                        Export
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {selectedSession && (
        <Card variant="red">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900">Assessment Details</h3>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setSelectedSession(null)}
            >
              Close
            </Button>
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Session Info</h4>
                <div className="text-sm text-gray-600 space-y-1">
                  <p><strong>ID:</strong> {selectedSession.id}</p>
                  <p><strong>Date:</strong> {formatTimestamp(selectedSession.created_at)}</p>
                  <p><strong>Persona:</strong> {selectedSession.persona}</p>
                  <p><strong>Model:</strong> {selectedSession.target_model}</p>
                </div>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Results</h4>
                <div className="text-sm text-gray-600">
                  {selectedSession.results && typeof selectedSession.results === 'object' ? (
                    <pre className="whitespace-pre-wrap bg-gray-50 p-2 rounded text-xs">
                      {JSON.stringify(selectedSession.results, null, 2)}
                    </pre>
                  ) : (
                    <p>No detailed results available</p>
                  )}
                </div>
              </div>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Base Prompt</h4>
              <div className="bg-gray-50 p-3 rounded text-sm text-gray-700">
                {selectedSession.base_prompt}
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};